import { Hourglass } from 'lucide-react';
import type { ReactNode } from 'react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { formatPercent } from '@/lib/money';
import { cn } from '@/lib/utils';
import { PROFIT_LEVEL_LABELS, type ProfitView } from '../api/products';
import { PROFIT_LEVEL_STYLES } from './profit-levels';

/**
 * Selo do lucro de um anúncio ou produto. **Sólido = conta completa; tracejado = provisório**: a
 * conta saiu sem algum custo (embalagem, tarifa), então a margem é um teto — a real é essa ou
 * menor. A ampulheta e o texto do selo dizem isso para quem não distingue a borda; o detalhe (o
 * que falta, faixa entre anúncios) vai na dica.
 */
export function ProfitBadge({
  view,
  margin,
  detail,
  size = 'sm',
  className,
}: {
  view: ProfitView;
  /** Margem (fração) a mostrar; ausente em "sem custo". */
  margin?: string;
  /** Linhas extras da dica (lucro em reais, faixa entre anúncios). */
  detail?: ReactNode;
  size?: 'sm' | 'lg';
  className?: string;
}) {
  const style = PROFIT_LEVEL_STYLES[view.level];
  const Icon = view.provisional ? Hourglass : style.icon;
  const label = PROFIT_LEVEL_LABELS[view.level];
  const hasMargin = view.level !== 'NO_COST' && margin !== undefined;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            tabIndex={0}
            data-level={view.level}
            data-provisional={view.provisional || undefined}
            variant={view.provisional ? style.soft : style.solid}
            dashed={view.provisional}
            className={cn(
              'shrink-0 whitespace-nowrap font-semibold tabular',
              size === 'lg' && 'px-2.5 py-1 text-sm [&_svg]:size-4',
              className,
            )}
          >
            <Icon aria-hidden />
            {hasMargin ? formatPercent(margin) : label}
            {size === 'lg' && hasMargin && <span className="font-medium">· {label}</span>}
            {view.provisional && <span className="font-medium">· provisório</span>}
            <span className="sr-only">{hasMargin ? ` ${label}.` : ''}</span>
          </Badge>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs space-y-1 text-left">
          <p className="font-semibold">{label}</p>
          {detail}
          {view.pending.length > 0 && (
            <p>
              {view.level === 'NO_COST'
                ? `Falta: ${view.pending.join(', ')}. Sem o custo de compra não há margem.`
                : `Provisório — falta: ${view.pending.join(', ')}. A margem real é essa ou menor.`}
            </p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
